import { ProgramId } from '@gyym/sdk'
import {
  cachedGyymPrefetch,
  composeServerProps,
  gyymPrefetch,
  withGyymAuth,
  withGyymSSR,
} from '@gyym/sdk-next'

import { SingleColumnLayout } from '@/components/Layouts/legacy/SingleColumnLayout'
import { ProgramDetailView } from '@/components/Views/ProgramDetailView'
import {
  buildFacilityLink,
  FACILITY_INDEX_TITLE,
  formatCityCode,
  getRequestedFacilityPath,
  isCanonicalFacilityPath,
  useCity,
} from '@/modules/Facilities'
import { getPrefectureById } from '@/modules/Prefectures'
import { useProgram } from '@/modules/Programs'

type ProgramDetailProps = {
  programId: ProgramId
}

export const getServerSideProps = composeServerProps(
  withGyymAuth(),
  withGyymSSR(async (ctx, client) => {
    const { programId } = ctx.query ?? {}

    const [programKey, program] = await gyymPrefetch(
      client.user.programs.get,
      Number(programId),
    )

    if (!program) {
      return {
        notFound: true,
      }
    }

    // Only the canonical URL is served. The canonical path is built from the
    // facility of the program, so a program of another facility is 404 too.
    const requestedPath = getRequestedFacilityPath(
      ctx.req.headers['x-pathname'],
      ctx.query,
      `/programs/${programId}`,
    )

    if (
      !isCanonicalFacilityPath(
        requestedPath,
        program.facility,
        `/programs/${program.id}`,
      )
    ) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        programId: program.id,
      },
      cache: [
        [programKey, program],
        /**
         * Commented out due to 'large page data' issue
         * @see https://nextjs.org/docs/messages/large-page-data
         */
        // gyymPrefetch(client.user.facilities.listTimetables, Number(facilityId)),
        cachedGyymPrefetch((c) => c.user.prefectures.list),
      ],
    }
  }),
)

/**
 * Program detail page component
 */
const ProgramDetail = ({ programId }: ProgramDetailProps) => {
  const { data } = useProgram(programId)
  const program = data!

  const facility = program.facility

  const prefecture = getPrefectureById(facility.prefectureId)!
  const city = useCity(`${facility.cityCode}`)!

  return (
    <SingleColumnLayout
      title={`${facility.name} - ${program.name} / ${facility.station} | GYYM (都度利用サービス)`}
      description={`【フィットネス都度利用サービスGYYM】${facility.name} - ${program.name} / ${facility.station} | 利用料金は利用した分だけ！ジム初心者やしっかり体験したい方も安心の入会金無料・ビジターOKの都度利用型のジムネットワークサービス`}
      keywords={[
        facility.name,
        facility.cityName,
        facility.station,
        'gyym',
        'GYYM',
        'ジム',
        'フィットネス',
        'スタジオ',
        'ジム 都度利用',
        'ジム 体験',
        'ジム 入会金無料',
        'ジム 初心者',
      ]}
      breadcrumbs={[
        { label: 'TOP', href: '/' },
        { label: FACILITY_INDEX_TITLE, href: '/facilities' },
        {
          label: prefecture.name,
          href: {
            pathname: `/facilities/[prefectureSlug]`,
            query: {
              prefectureSlug: prefecture.slug,
            },
          },
          as: `/facilities/${prefecture.slug}`,
        },
        {
          label: city.name,
          href: {
            pathname: `/facilities/[prefectureSlug]/cities/[cityCode]`,
            query: {
              prefectureSlug: prefecture.slug,
              cityCode: city.code,
            },
          },
          as: `/facilities/${prefecture.slug}/C${formatCityCode(city.code)}`,
        },
        {
          label: facility.name,
          ...buildFacilityLink(facility),
        },
        { label: program.name, href: '' },
      ]}
      breadcrumbsGrayBackground
    >
      <ProgramDetailView program={program} />
    </SingleColumnLayout>
  )
}

export default ProgramDetail
